import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  ArrowDown,
  MapPin,
  Clock,
  Briefcase,
  Globe,
  HeartPulse,
  BookOpen,
  Coins,
  Rocket,
  Sun,
  Mail,
  Users,
  Heart,
  Target,
  Zap,
  Handshake,
  Lightbulb,
  Shield,
  Smile,
  X,
} from 'lucide-react';
import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';

const benefits = [
  {
    icon: Globe,
    title: 'Remote-first',
    text: 'Work from anywhere with a team spread across time zones. We care about output, not office hours.',
  },
  {
    icon: HeartPulse,
    title: 'Health & wellness',
    text: 'Comprehensive health cover and a wellness budget so you can do your best work.',
  },
  {
    icon: BookOpen,
    title: 'Learning budget',
    text: 'Annual budget for courses, conferences, books, and anything that levels up your craft.',
  },
  {
    icon: Coins,
    title: 'Meaningful equity',
    text: 'Ownership shared with every full-time team member, because your work deserves a stake.',
  },
  {
    icon: Rocket,
    title: 'Ship fast',
    text: 'Small team, high ownership. You will see your code, design, or copy in production quickly.',
  },
  {
    icon: Sun,
    title: 'Work-life balance',
    text: 'Flexible hours, generous time off, and the freedom to work when you are at your best.',
  },
];

const culture = [
  {
    icon: Target,
    title: 'Customer first',
    text: 'Every decision starts with the support teams and customers we serve.',
  },
  {
    icon: Zap,
    title: 'Ship fast',
    text: 'Two-week cycles, clear outcomes, and no waiting for perfect.',
  },
  {
    icon: Shield,
    title: 'Built on trust',
    text: 'Honest feedback, transparent pay, and ownership from day one.',
  },
  {
    icon: Globe,
    title: 'Global from day one',
    text: 'African roots, worldwide reach, and a remote team that collaborates across borders.',
  },
];

const lifeAtZuriHub = [
  {
    icon: Users,
    title: 'Async by default',
    text: 'We write things down, record context, and meet only when it truly helps.',
  },
  {
    icon: Smile,
    title: 'Team rituals',
    text: 'Weekly demos, casual hangouts, and quarterly offsites to stay connected.',
  },
  {
    icon: Handshake,
    title: 'High ownership',
    text: 'You own outcomes end to end, from idea to shipped feature.',
  },
  {
    icon: Lightbulb,
    title: 'Learn in public',
    text: 'Share experiments, wins, and failures so the whole team grows.',
  },
];

const roles = [
  {
    title: 'Senior Frontend Engineer',
    dept: 'Engineering',
    loc: 'Remote',
    type: 'Full-time',
    description: 'Build modern, responsive interfaces for our AI-first customer experience platform. You will work with React, TypeScript, and Next.js to create seamless user experiences.',
    requirements: [
      '5+ years of frontend development experience',
      'Expert knowledge of React, TypeScript, and modern JavaScript',
      'Experience with Next.js, Tailwind CSS, and responsive design',
      'Strong understanding of performance optimization and accessibility',
      'Experience with testing frameworks (Jest, Cypress, or similar)',
      'Excellent problem-solving and communication skills',
    ],
    benefits: [
      'Work on the full frontend stack of a production AI platform',
      'Direct impact on thousands of businesses',
      'Flexible hours and remote-first culture',
      'Competitive salary and meaningful equity',
      'Learning budget for conferences and courses',
    ],
  },
  {
    title: 'Product Designer',
    dept: 'Design',
    loc: 'Remote',
    type: 'Full-time',
    description: 'Design intuitive, beautiful interfaces that simplify customer support workflows. You will own the design process from research to implementation.',
    requirements: [
      '4+ years of product design experience',
      'Expert proficiency in Figma and design systems',
      'Strong portfolio showcasing UI/UX work',
      'Experience with user research and testing',
      'Understanding of accessibility and inclusive design',
      'Excellent collaboration skills with engineering teams',
    ],
    benefits: [
      'Lead design for core product features',
      'Shape the visual identity of ZuriHub',
      'Flexible hours and remote-first culture',
      'Competitive salary and meaningful equity',
      'Access to premium design tools and resources',
    ],
  },
  {
    title: 'Customer Success Manager',
    dept: 'Success',
    loc: 'Remote',
    type: 'Full-time',
    description: 'Help our customers succeed by providing exceptional support, onboarding, and strategic guidance. You will be the bridge between our product and customer happiness.',
    requirements: [
      '3+ years of customer success or account management experience',
      'Experience with SaaS products and customer onboarding',
      'Strong communication and relationship-building skills',
      'Ability to translate customer feedback into product insights',
      'Experience with CRM tools and customer support platforms',
      'Proactive problem-solving mindset',
    ],
    benefits: [
      'Direct impact on customer retention and satisfaction',
      'Work with businesses across industries',
      'Flexible hours and remote-first culture',
      'Competitive salary and meaningful equity',
      'Growth opportunities within the company',
    ],
  },
  {
    title: 'Growth Marketer',
    dept: 'Marketing',
    loc: 'Remote',
    type: 'Full-time',
    description: 'Drive user acquisition and revenue growth through data-driven marketing campaigns. You will own the full funnel from awareness to conversion.',
    requirements: [
      '4+ years of growth marketing or performance marketing experience',
      'Strong analytical skills and experience with marketing analytics',
      'Experience with paid advertising (Google, Meta, LinkedIn)',
      'Understanding of SEO, content marketing, and conversion optimization',
      'Experience with marketing automation tools',
      'Creative mindset with data-driven decision making',
    ],
    benefits: [
      'Own significant marketing budget and campaigns',
      'Direct impact on company growth metrics',
      'Flexible hours and remote-first culture',
      'Competitive salary and meaningful equity',
      'Access to cutting-edge marketing tools',
    ],
  },
  {
    title: 'Community Manager',
    dept: 'Marketing',
    loc: 'Remote',
    type: 'Full-time',
    description: 'Build and nurture our community of customers, partners, and advocates. You will create engaging content, organize events, and foster meaningful connections.',
    requirements: [
      '3+ years of community management or social media experience',
      'Strong understanding of community building strategies',
      'Excellent written and verbal communication skills',
      'Experience with social media platforms and community tools',
      'Ability to create engaging content and facilitate discussions',
      'Passion for building relationships and connecting people',
    ],
    benefits: [
      'Lead community initiatives and events',
      'Shape ZuriHub\'s brand voice and presence',
      'Flexible hours and remote-first culture',
      'Competitive salary and meaningful equity',
      'Opportunity to grow with the company',
    ],
  },
  {
    title: 'Product Content Creator',
    dept: 'Marketing',
    loc: 'Remote',
    type: 'Full-time',
    description: 'Create compelling product content that educates and converts. You will write documentation, blog posts, case studies, and marketing materials.',
    requirements: [
      '3+ years of technical writing or product content experience',
      'Strong portfolio of B2B SaaS content',
      'Excellent writing and editing skills',
      'Ability to explain complex technical concepts simply',
      'Experience with SEO and content strategy',
      'Collaboration with product and engineering teams',
    ],
    benefits: [
      'Shape the narrative of an AI-first platform',
      'Work directly with product and engineering teams',
      'Flexible hours and remote-first culture',
      'Competitive salary and meaningful equity',
      'Learning budget for professional development',
    ],
  },
];

const jobFaqs = [
  {
    q: 'Is ZuriHub a remote-first company?',
    a: 'Yes. We are fully distributed and hire across Africa, Europe, and North America. You can work from anywhere with a reliable internet connection.',
  },
  {
    q: 'What is the interview process like?',
    a: 'It is simple and fast: a short application, a 30-minute culture call, a small take-home task, and a final conversation with the founder.',
  },
  {
    q: 'Do you offer equity to employees?',
    a: 'Yes. Every full-time team member receives meaningful equity because we want owners, not just employees.',
  },
  {
    q: 'What kind of people do well at ZuriHub?',
    a: 'People who love autonomy, move fast, and care deeply about customers. Kindness and clear writing matter as much as technical skill.',
  },
];

const pageDescription =
  'Join ZuriHub and help build the AI-first customer experience platform for modern teams. Remote-first jobs in engineering, design, customer success, marketing, and community management.';

export default function Careers() {
  const [selectedRole, setSelectedRole] = useState<typeof roles[0] | null>(null);
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Careers at ZuriHub',
      url: `${site.baseUrl}/careers`,
      description: pageDescription,
    },
    ...roles.map((role) => ({
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: role.title,
      description: `${role.description}\n\nRequirements:\n${role.requirements.join('\n')}\n\nBenefits:\n${role.benefits.join('\n')}`,
      datePosted: '2026-08-21',
      validThrough: '2026-12-31',
      employmentType: role.type,
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'Remote',
        },
      },
      hiringOrganization: {
        '@type': 'Organization',
        name: 'ZuriHub',
        sameAs: site.baseUrl,
      },
      jobLocationType: 'TELECOMMUTE',
      applicantLocationRequirements: {
        '@type': 'Country',
        name: 'Any',
      },
    })),
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: jobFaqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a,
        },
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Careers"
        description={pageDescription}
        path="/careers"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="ZuriHub careers, remote customer experience jobs, AI startup jobs Africa, work at ZuriHub, engineering careers, product designer jobs, customer success jobs, growth marketer remote, community manager jobs, product content creator jobs"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Globe className="h-4 w-4" /> Remote-first · High ownership
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Build the future of <span className="gradient-text">customer experience</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            Join a fast, remote team building an AI-first support platform for growing businesses everywhere.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="#open-roles"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              View open roles <ArrowDown className="h-4 w-4" />
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Our story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="bg-slate-50 py-20" aria-labelledby="culture-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="culture-heading" className="section-title">Our culture</h2>
            <p className="section-subtitle mx-auto">Four principles that guide how we work, hire, and ship.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {culture.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-slate-900">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="life-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="life-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                Life at ZuriHub
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                We are a small, ambitious team spread across countries. You will own real work, ship to production, and learn alongside kind, driven people.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Flexible hours built around your life.',
                  'Weekly demos to share progress and learnings.',
                  'Quarterly team offsites and virtual hangouts.',
                  'Health, wellness, and learning budgets.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Heart className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid gap-4">
              {lifeAtZuriHub.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-20 text-white" aria-labelledby="why-build-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="why-build-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                Why we build together
              </h2>
              <p className="mt-4 text-lg text-slate-300">
                Customer support is broken for growing teams. We are building the AI-first platform we wish we had: fast, affordable, and easy to love.
              </p>
              <p className="mt-4 text-slate-400">
                Every person on the team has a direct line to customers, a say in the roadmap, and a share in the outcome.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <Heart className="h-8 w-8 text-brand-300" />
              <p className="mt-4 text-2xl font-bold leading-relaxed">
                "We are building the tool we wish we had when we were scaling our own business."
              </p>
              <p className="mt-4 text-slate-300">Bahati Asher Faith, Founder</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="benefits-heading" className="section-title">Why work here</h2>
            <p className="section-subtitle mx-auto">Perks that match the way we work.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="flex flex-col items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{b.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="open-roles" className="py-20" aria-labelledby="roles-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="roles-heading" className="section-title">Open roles</h2>
            <p className="section-subtitle mx-auto">Apply by email or start a workspace to learn the product.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {roles.map((role) => (
              <div
                key={role.title}
                className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <h3 className="text-lg font-bold text-slate-900">{role.title}</h3>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1 font-medium text-brand-600">
                    <Briefcase className="h-4 w-4" /> {role.dept}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-4 w-4" /> {role.loc}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-4 w-4" /> {role.type}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedRole(role)}
                  className="mt-4 w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700"
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Details Modal */}
      {selectedRole && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedRole(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">{selectedRole.title}</h3>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1 font-medium text-brand-600">
                    <Briefcase className="h-4 w-4" /> {selectedRole.dept}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-4 w-4" /> {selectedRole.loc}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-4 w-4" /> {selectedRole.type}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedRole(null)}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-6 prose prose-sm max-w-none">
              <p className="text-slate-700 leading-relaxed">{selectedRole.description}</p>

              <div className="mt-6">
                <h4 className="mb-3 font-bold text-slate-900">Requirements</h4>
                <ul className="space-y-2">
                  {selectedRole.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-700">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <h4 className="mb-3 font-bold text-slate-900">Benefits</h4>
                <ul className="space-y-2">
                  {selectedRole.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-700">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-600" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <a
                href={`mailto:bahati@getzurihub.com?subject=Application%20for%20${encodeURIComponent(selectedRole.title)}&body=Hi%20team,%0D%0A%0D%0AI%20am%20interested%20in%20the%20${encodeURIComponent(selectedRole.title)}%20role.%20Please%20find%20my%20details%20below.%0D%0A%0D%0AThank%20you.`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
              >
                Apply <Mail className="h-4 w-4" />
              </a>
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-bold text-slate-700 transition hover:border-brand-300 hover:text-brand-700"
              >
                Try workspace <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      )}

      <section className="bg-slate-50 py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="mb-10 text-center text-3xl font-bold text-slate-900 sm:text-4xl">
            Careers FAQ
          </h2>
          <div className="space-y-4">
            {jobFaqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                open
              >
                <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-bold text-slate-900">
                  {faq.q}
                  <ArrowDown className="h-5 w-5 text-slate-400 transition group-open:rotate-180" />
                </summary>
                <p className="mt-4 leading-relaxed text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-20 text-white" aria-label="Apply now">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">No perfect role?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-300">
            We are always open to smart, kind people. Send your best work and we will be in touch.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="mailto:bahati@getzurihub.com?subject=General%20application"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Send your resume <Mail className="h-4 w-4" />
            </a>
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Explore workspace <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
